import {
  Box,
  Button,
  CardContent,
  Checkbox,
  FormControl,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  Typography,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { useEffect, useState } from "react";
import vendorService from "@/service/vender.service";
// import ToastService from "@/service/toast.service"; // Adjust path if needed
import { useDispatch } from "react-redux";
import ToastService from "@/service/toaster.service";
import { setAuthInfo } from "@/redux/auth/auth.slice";
// import { setAuthInfo } from "@/store/slices/auth"; // Adjust path if needed

const ServicesProvidedSection = ({
  handleChange,
  formData,
  errors,
  isAnyButtonInEditMode,
}) => {
  const [vendorType, setVendorType] = useState(formData?.vendor_type || "");
  const [availableServices, setAvailableServices] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedServices, setSelectedServices] = useState(formData?.services || []);
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData?.vendor_type) {
      setVendorType(formData.vendor_type);
    }
  }, [formData?.vendor_type]);

  const vendorTypes = [
    "Realestate Agent",
    "Conveyancer/Solicitor",
    "Building & Pest inspector",
    "Buyers agent",
    "Mortgage Broker",
    "Property Manager",
    "Accountant",
    "Building Surveyor"
  ];

  // Load services on vendor type change
  useEffect(() => {
    const fetchServices = async () => {
      if (!vendorType) return;
      try {
        const services = await vendorService.fetchServicesByType(vendorType);
        setAvailableServices(services);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      }
    };
    fetchServices();
  }, [vendorType]);

  // Toggle checkbox
  const handleCheckboxChange = (serviceId) => {
    setSelectedServices((prev) =>
      prev.some((s) => (typeof s === "object" ? s._id === serviceId : s === serviceId))
        ? prev.filter((s) => (typeof s === "object" ? s._id !== serviceId : s !== serviceId))
        : [...prev, serviceId]
    );
  };

  const handleSelectValue = (value) => {
    setVendorType(value);
    setSelectedServices([]);
  };

  // Save handler
  const handleSave = async () => {
    const data = new FormData();
    data.append("vendor_type", vendorType);

    if (!selectedServices || selectedServices.length === 0) {
      ToastService.info("Please select at least one service.");
      return;
    }
    selectedServices.forEach((s, index) => {
      data.append("services[]", typeof s === "object" ? s._id : s);
    });

    try {
      const res = await vendorService.updateVendorProfile(formData?._id, data);
      if (res?.data?.data) {
        dispatch(setAuthInfo({ ...res.data.data, role: "vendor", id: res.data.data._id }));
        ToastService.success("Profile updated successfully!");
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Update failed", err);
      ToastService.error("Failed to update profile.");
    }
  };

  return (
    <CardContent>
      {/* Vendor Type & Edit/Save Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Vendor Type Label + Select/Input in same line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Vendor Type
          </Typography>
          {isEditing ? (
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                name="vendor_type"
                value={vendorType}
                onChange={(e) => handleSelectValue(e.target.value)}
                size="small"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      width: 250,
                    },
                  },
                }}
              >
                {vendorTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body1" sx={{ ml: 1 }}>
              {formData?.vendor_type}
            </Typography>
          )}
        </Box>

        {/* Edit/Save Button */}
        <Button
          startIcon={
            isEditing ? (
              <SaveIcon sx={{ color: selectedServices.length ? "#1E40AF" : "grey" }} />
            ) : (
              <EditIcon sx={{ color: "grey" }} />
            )
          }
          size="small"
          sx={{
            borderRadius: 2,
            border: "1px solid #D3D3D3",
            backgroundColor: "transparent",
            color: isEditing && Object.keys(errors).length === 0
              ? "#1E40AF"
              : "grey",
            "&:hover": {
              borderColor: "#1E40AF",
              color: "#1E40AF",
            },
          }}
          onClick={isEditing ? handleSave : () => setIsEditing(true)}
          disabled={isAnyButtonInEditMode && !isEditing}
        >
          {isEditing ? "Save" : "Edit"}
        </Button>
      </Box>

      {/* Services Provided */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Services Provided
      </Typography>
      <Grid container spacing={2}>
        {isEditing
          ? availableServices.map((service) => (
            <Grid item xs={12} sm={6} key={service._id}>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <Checkbox
                    checked={selectedServices.some(
                      (s) => (typeof s === "object" ? s._id : s) === service._id
                    )}
                    onChange={() => handleCheckboxChange(service._id)}
                    color="primary"
                    sx={{ "&.Mui-checked": { color: "green" } }}
                  />
                </ListItemIcon>
                <ListItemText primary={service.servicesName} />
              </ListItem>
            </Grid>
          ))
          : formData?.services?.map((service) => (
            <Grid item xs={12} sm={6} key={service._id || service}>
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    typeof service === "object"
                      ? service.servicesName
                      : availableServices.find((s) => s._id === service)?.servicesName || service
                  }
                />
              </ListItem>
            </Grid>
          ))}
      </Grid>
    </CardContent>
  );
};

export default ServicesProvidedSection;



